/*!
 * ua.filter
 * Shared dashboard-style filter toolbar for searchable collections and lists.
 */

var _ = require("../../../uam/module.js")(module.exports, {
  namespace: "ua.filter",
  title: "Filter Toolbar",
  description: "Reusable search and filter toolbar with active filter chips and quick toggles.",
  control: true,
  fields: {
    name: {
      title: "Name",
      type: "string",
      description: "Logical name for the filter toolbar."
    },
    searchable: {
      title: "Searchable",
      type: "boolean",
      description: "Show the shared search input.",
      default: true
    },
    "search-placeholder": {
      title: "Search Placeholder",
      type: "string",
      description: "Placeholder text for the search field.",
      default: "Search..."
    },
    filters: {
      title: "Filters",
      type: "array",
      description: "Optional filter definitions. Rows support { name, label, description, options:[{value,label,icon}], icon, buttonLabel, emptyLabel, preview, multi, iconOnly, quickToggle }."
    },
    controls: {
      title: "Controls",
      type: "inner",
      description: "Optional controls rendered on the right side of the toolbar."
    },
    "show-active-filters": {
      title: "Show Active Filters",
      type: "boolean",
      description: "Render the active filter chip row.",
      default: true
    }
  },
  styling: {
    styles: [
      {
        selector: ".ua-dashboard-list-toolbar",
        styles: [
          "display: flex;",
          "align-items: center;",
          "gap: .5rem;",
          "flex-wrap: wrap;",
          "margin-bottom: .75rem;"
        ]
      },
      {
        selector: ".ua-dashboard-list-toolbar .ua-dashboard-search,.ua-dashboard-list-toolbar .ua-filter-search",
        styles: [
          "flex: 1 1 14rem;",
          "min-width: 10rem;"
        ]
      },
      {
        selector: ".ua-dashboard-toolbar-spacer",
        styles: [
          "flex: 1 1 auto;"
        ]
      },
      {
        selector: ".ua-dashboard-active-filters",
        styles: [
          "display: flex;",
          "align-items: center;",
          "gap: .5rem;",
          "flex-wrap: wrap;",
          "margin-bottom: 1rem;",
          "background: rgba(255, 255, 255, .08);",
          "border: .0625rem solid rgba(255, 255, 255, .08);",
          "border-radius: .85rem;"
        ]
      },
      {
        selector: ".ua-dashboard-active-filters.d-none",
        styles: [
          "display: none !important;"
        ]
      },
      {
        selector: ".ua-dashboard-quick-toggles",
        styles: [
          "display: inline-flex;",
          "align-items: center;",
          "gap: .35rem;",
          "flex-wrap: wrap;"
        ]
      },
      {
        selector: ".ua-dashboard-active-filter-chips",
        styles: [
          "display: inline-flex;",
          "align-items: center;",
          "gap: .35rem;",
          "flex-wrap: wrap;"
        ]
      },
      {
        selector: ".ua-dashboard-list-toolbar .ua-dashboard-filter-chip",
        styles: [
          "display: inline-flex;",
          "align-items: center;",
          "gap: .35rem;"
        ]
      },
      {
        selector: ".ua-filter .form-control,.ua-filter .btn",
        styles: [
          "box-shadow: none;"
        ]
      }
    ]
  }
});

var uai = require("../interface/interface.js");

function normalizeIconClass(icon) {
  var text = (icon || "").toString().trim();
  if (!text) return "";
  if (text.indexOf("bi ") === 0) return text;
  if (text.indexOf("bi-") === 0) return "bi " + text;
  return text;
}

function sanitizeFilterOptions(rawOptions) {
  var rows = rawOptions instanceof Array ? rawOptions : [];
  return rows.map(function(opt) {
    return {
      value: (opt.value || "").toString(),
      label: (opt.label || opt.value || "").toString(),
      description: (opt.description || "").toString(),
      icon: (opt.icon || "").toString(),
      thumbnail: (opt.thumbnail || opt.image || "").toString(),
      meta: (opt.meta || "").toString(),
      badge: (opt.badge || "").toString()
    };
  });
}

function toInner(value) {
  if (value instanceof Array) return value;
  if (value === undefined || value === null || value === "") return [];
  return [value];
}

exports.render = function(options) {
  var uid = uai.generateRandomID(8);
  var searchPlaceholder = (options["search-placeholder"] || "Search...").toString();
  var filters = options.filters instanceof Array ? options.filters : [];
  var controls = toInner(options.controls);
  var toolbarItems = [];
  var rightItems = [];
  var inlineQuickToggleItems = [];

  if (options.searchable !== false) {
    toolbarItems.push({
      n: "input",
      type: "search",
      c: "form-control ua-dashboard-search ua-filter-search",
      placeholder: searchPlaceholder,
      autocomplete: "off",
      "data-dashboard-search": "true",
      "data-collection-search": "true"
    });
  }

  for (var f = 0; f < filters.length; f++) {
    var filter = filters[f] || {};
    var filterName = (filter.name || ("filter-" + f)).toString();
    var filterLabel = (filter.label || filterName || "Filter").toString();
    var filterIcon = normalizeIconClass(filter.icon || "");
    var filterButtonLabel = (filter.buttonLabel || filterLabel).toString();
    var filterDescription = (filter.description || "").toString().trim();
    if (filterDescription.toLowerCase() === filterLabel.toLowerCase() || filterDescription.toLowerCase() === filterButtonLabel.toLowerCase()) {
      filterDescription = "";
    }
    var filterPreview = (filter.preview || "text").toString().trim().toLowerCase();
    var filterMulti = filter.multi === true;
    var filterIconOnly = filter.iconOnly === true;
    var quickToggle = filter.quickToggle === true || filter["quick-toggle"] === true;
    var rawOptions = sanitizeFilterOptions(filter.options || []);
    var emptyLabel = (filter.emptyLabel || ("All " + filterButtonLabel.toLowerCase())).toString();
    var optionsJson = "[]";

    try {
      optionsJson = JSON.stringify(rawOptions);
    } catch (error) {
      optionsJson = "[]";
    }

    toolbarItems.push({
      n: "input",
      type: "hidden",
      value: "",
      "data-dashboard-filter": filterName,
      "data-dashboard-filter-label": filterLabel,
      "data-dashboard-filter-quick-toggle": quickToggle ? "true" : "false"
    });

    if (quickToggle && rawOptions.length > 0 && rawOptions.length <= 2) {
      inlineQuickToggleItems.push({
        n: "ua.button.toggle",
        name: "quick-toggle-" + filterName,
        title: filterButtonLabel,
        icon: filterIcon || "bi bi-funnel",
        mode: filterIconOnly ? "icon" : "icon-text",
        iconOnly: filterIconOnly,
        style: "btn btn-outline-secondary btn-sm",
        options: [{
          name: "",
          title: emptyLabel,
          icon: normalizeIconClass(filterIcon || "bi bi-funnel")
        }].concat(rawOptions.map(function(row) {
          return {
            name: row.value,
            title: row.label || row.value,
            icon: normalizeIconClass(row.icon || filterIcon || "bi bi-toggle2-on")
          };
        })),
        value: "",
        "data-dashboard-quick-toggle": "true",
        "data-dashboard-filter-name": filterName,
        "data-dashboard-filter-label": filterLabel,
        "data-dashboard-filter-options": optionsJson,
        "data-dashboard-filter-empty-label": emptyLabel,
        "data-dashboard-filter-multi": "false"
      });
      continue;
    }

    if (quickToggle && rawOptions.length > 2) {
      var quickToggleDialogIconOnly = filterIconOnly === true;
      var quickToggleButton = {
        n: "button",
        type: "button",
        c: "btn btn-outline-secondary btn-sm ua-dashboard-filter-chip",
        title: filterButtonLabel,
        "aria-label": filterButtonLabel,
        "data-dashboard-filter-dialog": "true",
        "data-dashboard-filter-name": filterName,
        "data-dashboard-filter-label": filterLabel,
        "data-dashboard-filter-button-label": filterButtonLabel,
        "data-dashboard-filter-icon": normalizeIconClass(filterIcon || "bi bi-toggles"),
        "data-dashboard-filter-preview": filterPreview,
        "data-dashboard-filter-multi": filterMulti ? "true" : "false",
        "data-dashboard-filter-icon-only": quickToggleDialogIconOnly ? "true" : "false",
        "data-dashboard-filter-quick-toggle": "true",
        "data-dashboard-filter-options": optionsJson,
        "data-dashboard-filter-empty-label": emptyLabel,
        i: quickToggleDialogIconOnly
          ? { n: "i", c: normalizeIconClass(filterIcon || "bi bi-toggles") }
          : [
              filterIcon ? { n: "i", c: normalizeIconClass(filterIcon) } : "",
              filterButtonLabel
            ]
      };
      if (filterDescription) quickToggleButton["data-dashboard-filter-description"] = filterDescription;
      rightItems.push(quickToggleButton);
      continue;
    }

    var filterButton = {
      n: "button",
      type: "button",
      c: "btn btn-outline-secondary btn-sm ua-dashboard-filter-chip",
      title: filterButtonLabel,
      "aria-label": filterButtonLabel,
      "data-dashboard-filter-dialog": "true",
      "data-dashboard-filter-name": filterName,
      "data-dashboard-filter-label": filterLabel,
      "data-dashboard-filter-button-label": filterButtonLabel,
      "data-dashboard-filter-icon": filterIcon,
      "data-dashboard-filter-preview": filterPreview,
      "data-dashboard-filter-multi": filterMulti ? "true" : "false",
      "data-dashboard-filter-icon-only": filterIconOnly ? "true" : "false",
      "data-dashboard-filter-options": optionsJson,
      i: [
        filterIcon ? { n: "i", c: filterIcon } : "",
        filterIconOnly ? "" : filterButtonLabel
      ]
    };
    if (filterDescription) filterButton["data-dashboard-filter-description"] = filterDescription;
    toolbarItems.push(filterButton);
  }

  toolbarItems.push({
    n: "span",
    c: "ua-dashboard-toolbar-spacer",
    i: ""
  });

  toolbarItems = toolbarItems.concat(rightItems).concat(controls);

  return {
    n: "div",
    c: "ua-filter",
    "data-ua-filter": "true",
    "data-filter-name": (options.name || ("uaFilter" + uid)).toString(),
    i: [
      {
        n: "div",
        c: "ua-dashboard-list-toolbar",
        i: toolbarItems
      },
      options["show-active-filters"] === false ? "" : {
        n: "div",
        c: "ua-dashboard-active-filters bg-og-75 bg-og-75-background rounded d-none p-2",
        "data-dashboard-active-filters-row": "true",
        "data-collection-active-filters-row": "true",
        i: [
          {
            n: "button",
            type: "button",
            c: "btn btn-outline-secondary btn-sm d-none",
            title: "Clear all filters",
            "aria-label": "Clear all filters",
            "data-dashboard-clear-filters-row": "true",
            "data-collection-clear-filters-row": "true",
            i: { n: "i", c: "bi bi-eraser" }
          },
          {
            n: "div",
            c: "ua-dashboard-quick-toggles",
            "data-dashboard-quick-toggles": "true",
            "data-collection-quick-toggles": "true",
            i: inlineQuickToggleItems
          },
          {
            n: "div",
            c: "ua-dashboard-active-filter-chips",
            "data-dashboard-active-filter-chips": "true",
            "data-collection-active-filter-chips": "true",
            i: ""
          }
        ]
      }
    ]
  };
};
